import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, MapPin, Clock, Phone, Mail, CreditCard, ArrowRight } from "lucide-react";
import { COMPANY_INFO, FAQ_DATA, fadeInUp, staggerContainer } from "../data/constants";

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="py-24 bg-gray-50" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-orange-500 font-semibold uppercase tracking-wider"
          >
            Preguntas Frecuentes
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-gray-900 mt-4"
          >
            ¿Tienes dudas?
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="bg-white rounded-3xl shadow-sm overflow-hidden"
        >
          {FAQ_DATA.map((item, index) => (
            <motion.div key={index} variants={fadeInUp} className="faq-item">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                data-testid={`faq-item-${index}`}
              >
                <span className="font-semibold text-gray-900 pr-8">{item.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-orange-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600">{item.answer}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-white rounded-3xl p-8 shadow-sm"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Centro de Atención</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Ubicación</div>
                <div className="text-gray-600">{COMPANY_INFO.officeAddress}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Horarios</div>
                <div className="text-gray-600">{COMPANY_INFO.hours.weekdays}</div>
                <div className="text-gray-600">{COMPANY_INFO.hours.saturday}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Teléfono</div>
                <div className="text-gray-600">{COMPANY_INFO.phone}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Correo</div>
                <div className="text-gray-600">{COMPANY_INFO.supportEmail}</div>
              </div>
            </div>
          </div>
          
          {/* Payment methods */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-5 h-5 text-orange-500" />
              <span className="font-semibold text-gray-900">Formas de Pago</span>
            </div>
            <p className="text-gray-600">
              Transferencias, Depósitos bancarios, Pago en Centro de Atención y con Distribuidores Autorizados.
            </p>
          </div>

          {/* Portal de Clientes CTA */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
              <h4 className="text-xl font-bold mb-2">Portal de Clientes</h4>
              <p className="text-white/90 mb-4">
                Consulta tu saldo, realiza pagos y levanta tickets de soporte técnico.
              </p>
              <a
                href="https://portal.flowing.lat"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                data-testid="portal-cta"
              >
                Acceder al Portal
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
